/* PlannerCaseBriefCard — iter98.99br (rebuilt to match user mockup)
 *
 * Section-stack design on the planner's Submission tab. Each section
 * is a white card with a light-gray header bar that carries a 3px
 * primary-green left accent. Field values render inside soft gray
 * boxes (mirrors the doctor-side "memo paper" feel from the wizard).
 *
 * Sections (in order):
 *   1. Patient Information
 *   2. Case Details
 *   3. Clinical Assessment
 *   4. Extractions & Treatment Plan
 *   5. Retention Plan
 *
 * No collapse on individual sections — the planner reads top-down on
 * the Submission tab and the whole pane scrolls. The page header
 * already has a status badge + actions; this card is body content.
 */
import React, { useState, useEffect } from "react";
import { CaretDown } from "@phosphor-icons/react";


const CASE_TYPE_LABEL = {
    full: "Full", lite: "Lite", single_arch: "Single arch", express: "Express",
};
const ARCH_LABEL = { upper: "Upper", lower: "Lower", both: "Both arches" };
const EXTRACTION_LABEL = {
    none: "No extractions", planned: "Extractions planned",
    already_extracted: "Already extracted",
};
const PREF_LABEL = {
    avoid: "Avoid", minimal: "Minimal", as_needed: "As needed",
    aggressive: "Aggressive", minimise: "Minimise", standard: "Standard",
    class_ii: "Class II", class_iii: "Class III", not_needed: "Not needed",
};
const OBJECTIVE_LABEL = {
    alignment: "Alignment", bite_correction: "Bite correction",
    midline_correction: "Midline correction", crowding: "Crowding",
    spacing: "Spacing", deep_bite: "Deep bite", open_bite: "Open bite",
    crossbite: "Crossbite",
};
const RETAINER_LABEL = {
    vivera: "Clear · Vivera-style", essix: "Clear · Essix",
    hawley: "Hawley acrylic", fixed: "Fixed lingual bonded",
    none: "No retainer", clear: "Clear (generic)",
};


const humanise = (map, v) => (v ? (map[v] || String(v).replace(/_/g, " ")) : null);
const fmtMm = (v) => (v === null || v === undefined || v === "" ? null : `${v} mm`);
const fmtDob = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "numeric", month: "short", year: "numeric",
        });
    } catch { return iso; }
};


export default function PlannerCaseBriefCard({ c, startNumber = 1, onSectionCount = null }) {
    const cl = c?.clinical || {};
    const rx = c?.prescription || {};
    const plan = c?.plan || {};

    // ── Section data ────────────────────────────────────────────────
    // iter98.99by — Patient Information accordion removed (identity is
    // already in the page header). Medical history + Previous
    // orthodontics moved into Case Details so the planner has all
    // clinical context in one place.
    const objectives = Array.isArray(rx.objectives) ? rx.objectives : [];
    const caseRows = [
        cl.chief_complaint && { label: "Chief Complaint", value: cl.chief_complaint, span: 1, multiline: true },
        c.diagnosis && { label: "Diagnosis", value: c.diagnosis, span: 1, multiline: true },
        {
            label: "Malocclusion",
            value: [
                cl.malocclusion_class && `Class ${cl.malocclusion_class}`,
                humanise({ class_i: "Class I", class_ii: "Class II", class_iii: "Class III" }, cl.skeletal_class)
                    && `· Skeletal ${humanise({ class_i: "I", class_ii: "II", class_iii: "III" }, cl.skeletal_class)}`,
            ].filter(Boolean).join(" ") || null,
            span: 1,
        },
        {
            label: "Arch & Crowding",
            value: [
                humanise(ARCH_LABEL, cl.arch || c.arches),
                cl.crowding_distribution && `· ${cl.crowding_distribution} crowding`,
            ].filter(Boolean).join(" ") || null,
            span: 1,
        },
        cl.medical_history && { label: "Medical History", value: cl.medical_history, span: 2, multiline: true },
        cl.previous_ortho && {
            label: "Previous Orthodontics",
            value: [
                cl.previous_ortho,
                cl.previous_ortho_notes,
            ].filter(Boolean).join(" — "),
            span: 2,
            multiline: true,
        },
    ].filter(Boolean);

    const overjet = fmtMm(cl.overjet_mm);
    const overbite = fmtMm(cl.overbite_mm);
    const upperCrowd = fmtMm(cl.upper_arch_crowding_mm);
    const lowerCrowd = fmtMm(cl.lower_arch_crowding_mm);
    const clinicalRows = [
        overjet && { label: "Overjet", value: overjet, span: 1 },
        overbite && { label: "Overbite", value: overbite, span: 1 },
        upperCrowd && { label: "Upper Crowding", value: upperCrowd, span: 1 },
        lowerCrowd && { label: "Lower Crowding", value: lowerCrowd, span: 1 },
        cl.angle_class_right && {
            label: "Angle Classification",
            value: `R: ${humanise({ class_i: "I", class_ii: "II", class_iii: "III" }, cl.angle_class_right) || "—"} · L: ${humanise({ class_i: "I", class_ii: "II", class_iii: "III" }, cl.angle_class_left) || "—"}`,
            span: 1,
        },
        cl.midline_relationship && {
            label: "Midline",
            value: humanise({
                coincident: "Coincident",
                shifted_upper_left: "Shifted upper left",
                shifted_upper_right: "Shifted upper right",
                both_shifted: "Both shifted",
            }, cl.midline_relationship),
            span: 1,
        },
        cl.periodontal_health && {
            label: "Periodontal Health",
            value: humanise({
                excellent: "Excellent · No inflammation",
                good: "Good", fair: "Fair", poor: "Poor", critical: "Critical",
            }, cl.periodontal_health),
            span: 1,
        },
        cl.scan_device && {
            label: "Scan Source",
            value: humanise({
                itero: "iTero", trios: "3Shape Trios", medit: "Medit",
                primescan: "Primescan", impression: "Conventional impressions",
                other: "Other",
            }, cl.scan_device),
            span: 1,
        },
        cl.special_notes && {
            label: "Doctor's Clinical Notes", value: cl.special_notes, span: 2, multiline: true,
        },
    ].filter(Boolean);

    const isExtractionFlow = cl.extraction_plan === "planned";
    const extractionRows = [
        {
            label: "Extraction Plan",
            value: humanise(EXTRACTION_LABEL, cl.extraction_plan) || "Not specified",
            span: 1,
        },
        cl.extraction_teeth && { label: "Extraction Teeth", value: cl.extraction_teeth, span: 1 },
        cl.missing_teeth && { label: "Missing Teeth (existing)", value: cl.missing_teeth, span: 2 },
        (rx.ipr_preference || rx.attachments_preference || rx.elastics_preference) && {
            label: "Treatment Preferences",
            value: [
                rx.ipr_preference && `IPR: ${humanise(PREF_LABEL, rx.ipr_preference)}`,
                rx.attachments_preference && `Attachments: ${humanise(PREF_LABEL, rx.attachments_preference)}`,
                rx.elastics_preference && `Elastics: ${humanise(PREF_LABEL, rx.elastics_preference)}`,
                rx.material_preference && `Material: ${humanise(PREF_LABEL, rx.material_preference)}`,
            ].filter(Boolean).join(" · "),
            span: 2,
            multiline: true,
        },
        rx.do_not_touch_teeth && { label: "Do-not-touch Teeth", value: rx.do_not_touch_teeth, span: 1 },
        rx.esthetic_goals && { label: "Esthetic Goals", value: rx.esthetic_goals, span: 2, multiline: true },
        rx.lab_notes && { label: "Lab Notes from Doctor", value: rx.lab_notes, span: 2, multiline: true },
        c.treatment_notes && { label: "Doctor's Treatment Notes", value: c.treatment_notes, span: 2, multiline: true },
    ].filter(Boolean);

    const retentionRows = [
        plan.retainer_type && {
            label: "Preferred Retainer Type",
            value: humanise(RETAINER_LABEL, plan.retainer_type),
            span: 1,
        },
        plan.retention_schedule && {
            label: "Retention Schedule", value: plan.retention_schedule, span: 1, multiline: true,
        },
        plan.retention_recall_first_months && {
            label: "First Recall",
            value: `${plan.retention_recall_first_months} months after delivery`,
            span: 1,
        },
        plan.retention_recall_second_months && {
            label: "Second Recall",
            value: `${plan.retention_recall_second_months} months after delivery`,
            span: 1,
        },
    ].filter(Boolean);

    /* iter98.99cb — Sections rendered as an explicit ordered list so
       numbering stays sequential when conditional sections are
       skipped (e.g. no clinical data, no extractions). Each section
       carries its own pre-computed `number` so the planner reads a
       continuous 01 → 0N stepper down the Submission tab. */
    const sections = [
        {
            id: "case-details",
            title: "Case Details",
            body: (
                <>
                    <FieldGrid rows={caseRows} />
                    {objectives.length > 0 && (
                        <div className="mt-4">
                            <FieldLabel>Treatment Objectives</FieldLabel>
                            <div className="flex flex-wrap gap-2 mt-2" data-testid="planner-brief-objectives">
                                {objectives.map((o) => (
                                    <span
                                        key={o}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                                    >
                                        <span className="text-[10px]">✓</span>
                                        {humanise(OBJECTIVE_LABEL, o)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ),
        },
        clinicalRows.length > 0 && {
            id: "clinical-assessment",
            title: "Clinical Assessment",
            body: <FieldGrid rows={clinicalRows} />,
        },
        (isExtractionFlow || extractionRows.length > 1) && {
            id: "extractions-plan",
            title: "Extractions & Treatment Plan",
            body: <FieldGrid rows={extractionRows} />,
        },
        retentionRows.length > 0 && {
            id: "retention-plan",
            title: "Retention Plan",
            body: <FieldGrid rows={retentionRows} />,
        },
    ].filter(Boolean).map((s, idx) => ({ ...s, number: (startNumber || 1) + idx }));

    /* iter98.99cd — Bubble the rendered section count up so the parent
       can compute downstream numbering (Per-tooth + Records) without
       duplicating visibility logic here. */
    useEffect(() => {
        if (typeof onSectionCount === "function") onSectionCount(sections.length);
    }, [sections.length, onSectionCount]);

    return (
        <div className="space-y-4" data-testid="planner-case-brief-card">
            {sections.map((s) => (
                <Section
                    key={s.id}
                    title={s.title}
                    testId={s.id}
                    number={s.number}
                    caseId={c?.id}
                >
                    {s.body}
                </Section>
            ))}
        </div>
    );
}


/* iter98.99cd — Deprecated synchronous helper retained for callers that
   need a starting estimate (e.g. SSR or initial render). Underestimates
   on purpose — once the component mounts, the real count is bubbled up
   via `onSectionCount`. */
export function countBriefSections() {
    return 1;
}


/* Section card — collapsible accordion. Header is clickable, body
   slides open/closed. State persisted per-case per-section in
   localStorage so the planner's preference doesn't reset on
   navigation. iter98.99bt — accordions per user direction.
   iter98.99cb — optional `number` renders a small mono "01" prefix
   matching the Author-the-plan stepper rhythm. */
/* iter98.99cv — User direction: "Please collapse all accordions in the
   Doctor's Submission tab of the Planner portal when the planner opens
   it first time. Same to be applied to other tabs."
   Changed defaultOpen from `true` to `false` so every section starts
   collapsed. localStorage persistence is kept so once a planner expands
   a section, that choice survives navigation. */
function Section({ title, children, testId, caseId, number, defaultOpen = false }) {
    /* iter98.99cv — Storage key prefix bumped (v2) so existing
       localStorage entries from when defaultOpen was true don't
       keep sections expanded. Every planner gets the collapsed
       experience on the next case visit. */
    const storageKey = `plannerBrief2:${caseId || "default"}:${testId}`;
    const [open, setOpen] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            return v === null ? defaultOpen : v === "1";
        } catch { return defaultOpen; }
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, open ? "1" : "0"); } catch { /* noop */ }
    }, [open, storageKey]);
    return (
        <div
            className="bg-card rounded-2xl border border-border/70 overflow-hidden shadow-[0_1px_0_rgba(15,23,42,0.02),0_6px_18px_-14px_rgba(15,23,42,0.10)]"
            data-testid={`planner-section-${testId}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full px-5 py-3 border-b border-border/60 flex items-center gap-3 hover:bg-teal-50/40 transition-colors text-left group"
                style={{ background: "linear-gradient(to right, rgba(20,184,166,0.05) 0%, transparent 55%)" }}
                aria-expanded={open}
                data-testid={`planner-section-${testId}-toggle`}
            >
                {number != null && (
                    <span className="font-display text-[13px] font-bold tabular-nums shrink-0 w-6 text-teal-700">
                        {String(number).padStart(2, "0")}
                    </span>
                )}
                <h3 className="text-[11px] font-bold tracking-[0.14em] uppercase text-teal-900/80 flex-1">
                    {title}
                </h3>
                <CaretDown
                    size={13}
                    weight="bold"
                    className={`text-teal-700 transition-transform group-hover:scale-110 ${open ? "" : "-rotate-90"}`}
                />
            </button>
            {open && <div className="p-5">{children}</div>}
        </div>
    );
}


/* FieldGrid — renders a list of {label, value, span, multiline} into a
   2-col grid. `span: 2` (or rows with multiline content) take the full
   row. Empty values render "—" so the grid alignment doesn't shift. */
function FieldGrid({ rows }) {
    if (rows.length === 0) return null;
    return (
        <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">
            {rows.map((r, i) => (
                <FieldBlock
                    key={`${r.label}-${i}`}
                    label={r.label}
                    value={r.value}
                    span={r.span === 2}
                    multiline={r.multiline}
                />
            ))}
        </div>
    );
}


function FieldBlock({ label, value, span, multiline }) {
    return (
        <div className={span ? "sm:col-span-2" : ""}>
            <FieldLabel>{label}</FieldLabel>
            <div
                className={`mt-1.5 px-3 py-2.5 rounded-lg bg-secondary/40 border border-border/50 text-sm text-foreground/90 ${
                    value ? "" : "italic text-muted-foreground/60"
                } ${multiline ? "whitespace-pre-line" : ""}`}
            >
                {value || "—"}
            </div>
        </div>
    );
}


function FieldLabel({ children }) {
    return (
        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-muted-foreground">
            {children}
        </div>
    );
}
